import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { NumberParam, useQueryParam, withDefault } from 'use-query-params';
import { Box, CircularProgress, Grid, Typography } from '@material-ui/core';
import { InsertDriveFileOutlined as InsertDriveFileOutlinedIcon } from '@material-ui/icons';

import AppSnackbar from '../../common/AppSnackbar';
import { FetchError } from '../../common/api/api';
import { formatDate } from '../../common/formatter';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateAlert from '../../common/page-template/PageTemplateAlert';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import ErrorView from '../../common/views/ErrorView';
import environment from '../../environments';
import { useUserContext } from '../../user/User.context';
import { UserPermissionFilter } from '../../user/User.interface';
import { Task } from '../tasks/Task.interface';
import TaskStatusActions from '../tasks/TaskStatusActions';
import SubTaskListCard from '../tasks/cards/SubTaskListCard';
import TaskAssignCard from '../tasks/cards/TaskAssignCard';
import TaskAttachmentCard from '../tasks/cards/TaskAttachmentCard';
import TaskDueDateCard from '../tasks/cards/TaskDueDateCard';
import TaskFileListCard from '../tasks/cards/TaskFileListCard';
import { parseTaskStatus } from '../tasks/task.helper';
import { useTaskEntityFields } from '../workflow/Workflow.hooks';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import {
  useDocumentUploadData,
  useFindDocumentByTaskId
} from './Document.hook';
import { BaseDocumentCardProps, FileDto } from './Document.interface';
import DocumentChangeOrderV2ReferenceCard from './cards-v2/DocumentChangeOrderV2ReferenceCard';
import DocumentBreadcrumbs from './components/DocumentBreadcrumbs';
import DocumentChangeOrderReferenceCard from './components/DocumentChangeOrderReferenceCard';
import DocumentDescriptionOfChangeCard from './components/DocumentDescriptionOfChangeCard';
import { DocumentInternalIdCard } from './components/DocumentInternalIdCard';
import DocumentLogStatusCard from './components/DocumentLogStatusCard';
import DocumentOwnerCard from './components/DocumentOwnerCard';
import DocumentReasonForChangeCard from './components/DocumentReasonForChangeCard';
import { DocumentRevCard } from './components/DocumentRevCard';
import DocumentViewerCard from './components/DocumentViewerCard';

const StyledGrid = styled(Grid)`
  max-height: 100%;
  flex: 1;
  .grid-flex,
  .card-flex,
  .card-content-flex {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .cards {
    height: 100%;
    overflow: auto;
  }
`;

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
  .status-container {
    display: flex;
    margin-top: 5px;
    .status-label {
      margin-right: 8px;
    }
  }
`;

const DocumentDetailPage: React.FC = () => {
  const { taskId: paramTaskId = '' } = useParams<{ taskId?: string }>();
  const taskId: number = parseInt(paramTaskId) || 0;
  const [versionId] = useQueryParam('ver', withDefault(NumberParam, undefined));
  const fetchLogsRef = useRef<Function | undefined>();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const [error, setError] = useState<FetchError | undefined>();
  const [document, setDocument] = useState<FileDto | undefined>();
  console.debug('DocumentDetailPage document:', document);
  const { permissions } = useUserContext();
  const { file: can, isDocViewer } = permissions;
  const [{ data: fetchedDocument, isLoading, error: findError }, getDocument] =
    useFindDocumentByTaskId(taskId || 0, versionId);
  const versionLog = document?.task?.versionLog;
  const version =
    versionLog?.version || document?.task?.version || document?.version || 1;
  console.debug('DocumentDetailPage version:', version);
  const isLatest = !!versionLog?.isLatest;
  const { uploadDate, uploaderName, approvedDate } =
    useDocumentUploadData(document);
  // file should have fileVersion if specific version
  // other than the current version is requested
  const hasDocument = !!document && (isLatest || !!document.fileVersion);
  // only show warning if document exists
  const { isFieldDisabled } = useTaskEntityFields<WorkflowEntity.File>(
    document?.task,
    hasDocument
  );

  const storageKey: string | undefined =
    document?.fileVersion?.storageKey ?? document?.storageKey;
  console.debug('DocumentDetailPage storageKey:', storageKey);

  const latestText: string = isLatest ? 'latest' : 'previous';
  const status: string = parseTaskStatus(
    document?.task?.status || 'Unknown Status'
  ).toUpperCase();
  const task = document?.fileVersion?.task || document?.task;

  const userFilter: UserPermissionFilter | undefined = useMemo(() => {
    // allow users that have Admin Roles or part of File Groups
    const groupIds = (document?.metadata?.groups || []).map(group => group.id);
    return groupIds.length > 0
      ? { groupIds, roles: ['Admin', 'SiteAdmin', 'DocumentAdmin'] }
      : undefined;
  }, [document]);

  const cardProps: BaseDocumentCardProps = {
    document: document!,
    onChange: (newDocument: Partial<FileDto>) => {
      document && setDocument({ ...document, ...newDocument });
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleTaskChange = (newTask: Partial<Task>) => {
    if (!document?.task) {
      return;
    }
    Object.assign(document.task, newTask);
    cardProps.onChange?.({});
    cardProps.onUpdate?.();
  };

  useEffect(() => {
    getDocument();
  }, [getDocument]);

  useEffect(() => {
    fetchedDocument && setDocument(fetchedDocument);
  }, [fetchedDocument]);

  useEffect(() => {
    setError(findError);
  }, [findError]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        {isLoading && <CircularProgress size={20} />}
        {document && (
          <StyledTitle>
            <DocumentBreadcrumbs
              documentId={document.id}
              root={{ label: 'Documents', path: '/portal/documents' }}
              defaultData={{
                label: document.task?.key || `DOC-${document.id}`,
                path: `/portal/documents/file/task/${document.taskId}`
              }}
            />
            <h2 title={document.name}>{document.name}</h2>
          </StyledTitle>
        )}
        <PageTemplateHeaderActions flex>
          {isLatest && hasDocument && document?.task && (
            <TaskStatusActions
              task={document.task}
              taskEntity={document}
              disabled={can.not.update}
              onChange={() => getDocument()}
            />
          )}
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      {hasDocument && isDocViewer && (
        <PageTemplateAlert severity={isLatest ? 'success' : 'warning'}>
          You are viewing {isLatest ? 'the last approved' : 'a previous'}{' '}
          version of the document (v
          {version ?? '?'}) Approved on:{' '}
          {formatDate(approvedDate || uploadDate)}
        </PageTemplateAlert>
      )}
      {hasDocument && !isDocViewer && (
        <PageTemplateAlert severity={isLatest ? 'success' : 'warning'}>
          Viewing {latestText} document version {version ?? '?'}
          {!isLatest ? '.' : <>, status: {status},</>} Uploaded by{' '}
          {uploaderName} on {formatDate(uploadDate)}{' '}
          {!isLatest && (
            <Link to={`/portal/documents/file/task/${taskId}`}>
              Click here to view latest version
            </Link>
          )}
        </PageTemplateAlert>
      )}
      <PageTemplateDashboard>
        {isLoading && (
          <Box
            height={300}
            width='100%'
            display='flex'
            alignItems='center'
            justifyContent='center'
          >
            <CircularProgress />
          </Box>
        )}

        {!isLoading && !hasDocument && (
          <ErrorView
            title={
              findError && findError.statusCode === 401
                ? 'Insufficient Permissions'
                : document
                  ? 'Document Version Not Found'
                  : 'Document Not Found'
            }
            icon={
              <InsertDriveFileOutlinedIcon
                color='disabled'
                fontSize='inherit'
              />
            }
            description={
              !document ? (
                findError?.message || 'This document does not exist.'
              ) : (
                <Typography variant='body1' color='textSecondary'>
                  This version does not exist. View{' '}
                  <Link to={`/portal/documents/file/task/${taskId}`}>
                    latest version
                  </Link>{' '}
                  instead.
                </Typography>
              )
            }
          />
        )}

        {!isLoading && document && hasDocument && (
          <StyledGrid container spacing={3}>
            <Grid item xs={12} md={8} className='grid-flex'>
              {storageKey && (
                <DocumentViewerCard
                  {...cardProps}
                  title={document.name}
                  storageKey={storageKey}
                  fileSize={document.size}
                  onUpdate={() => {
                    fetchLogsRef.current?.();
                    cardProps.onUpdate?.();
                  }}
                />
              )}
            </Grid>

            <Grid item xs={12} md={4} className='cards'>
              <Grid container spacing={3}>
                {document.task && (
                  <Grid item xs={12}>
                    <DocumentLogStatusCard document={document} />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <DocumentOwnerCard {...cardProps} />
                </Grid>
                {!environment.fileConfig?.hideInternalDocumentId && (
                  <Grid item xs={12}>
                    <DocumentInternalIdCard {...cardProps} />
                  </Grid>
                )}
                {!environment.fileConfig?.hideRev && (
                  <Grid item xs={12}>
                    <DocumentRevCard {...cardProps} />
                  </Grid>
                )}
                {!environment.fileConfig?.hideDescriptionOfChange && (
                  <Grid item xs={12}>
                    <DocumentDescriptionOfChangeCard {...cardProps} />
                  </Grid>
                )}
                {!environment.fileConfig?.hideReasonForChange && (
                  <Grid item xs={12}>
                    <DocumentReasonForChangeCard {...cardProps} />
                  </Grid>
                )}
                {task?.children && task.children.length > 0 && (
                  <Grid item xs={12}>
                    <SubTaskListCard
                      task={task}
                      title='Stakeholders'
                      dialogTitle='Assign Approvers'
                      disabled={
                        !isLatest ||
                        can.not.update ||
                        isFieldDisabled('assignSubTasks')
                      }
                      onError={cardProps.onError}
                      onChange={handleTaskChange}
                    />
                  </Grid>
                )}
                {document.task && (
                  <Grid item xs={12}>
                    <TaskAttachmentCard
                      task={document.task}
                      disabled={can.not.update || !isLatest}
                      attachments={
                        !isLatest
                          ? document?.fileVersion?.task?.attachments || []
                          : undefined
                      }
                      onError={cardProps.onError}
                      onChange={handleTaskChange}
                    />
                  </Grid>
                )}
                {task && (
                  <Grid item xs={12}>
                    <TaskAssignCard
                      task={task}
                      disabled={can.not.update}
                      userFilter={userFilter}
                    />
                  </Grid>
                )}
                {document.task && (
                  <>
                    <Grid item xs={12}>
                      <TaskFileListCard
                        task={document.task}
                        fileReferences={document.fileVersion?.referenceFileIds}
                        disabled={can.not.update || !isLatest}
                        onError={cardProps.onError}
                        onChange={handleTaskChange}
                      />
                    </Grid>
                    {!environment.fileConfig?.hideChangeOrderReferenceCard && (
                      <Grid item xs={12}>
                        <DocumentChangeOrderReferenceCard
                          document={document}
                          version={versionId}
                          onError={cardProps.onError}
                        />
                      </Grid>
                    )}
                    {environment.fileConfig?.showChangeOrderV2ReferenceCard && (
                      <Grid item xs={12}>
                        <DocumentChangeOrderV2ReferenceCard
                          document={document}
                          version={versionId}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <TaskDueDateCard
                        task={document.task}
                        disabled={can.not.update || !isLatest}
                        onError={cardProps.onError}
                        onChange={handleTaskChange}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
          </StyledGrid>
        )}
      </PageTemplateDashboard>
      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={!!(didUpdate && document)}
        onClose={() => setDidUpdate(false)}
      >
        Document "{document?.name}" saved.
      </AppSnackbar>
    </PageTemplate>
  );
};

export default DocumentDetailPage;
